import type { EventSummaryResponse } from '@pulsify/shared/dto'
import { BarChart3, History } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent } from '~/entities/event/model/use-events'
import { useEventSummary } from '~/entities/stats/model/use-event-summary'
import { EventAnalyticsChart } from '~/features/events/components/event-analytics-chart'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import type { Event } from '~/shared/types'
import { Card } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { Skeleton } from '~/shared/ui/skeleton'

type ViewSource = EventSummaryResponse['views']['bySource'][number]['source']

const ANCHOR = 'rueckblick'
const HOUR_MS = 3_600_000

function sourceLabel(source: ViewSource): string {
  switch (source) {
    case 'app':
      return m.summarySourceApp()
    case 'search':
      return m.summarySourceSearch()
    case 'instagram':
      return m.summarySourceInstagram()
    case 'facebook':
      return m.summarySourceFacebook()
    case 'share_link':
      return m.summarySourceShareLink()
    case 'other_site':
      return m.summarySourceOtherSite()
    case 'pulsify':
      return m.summarySourcePulsify()
    case 'direct':
      return m.summarySourceDirect()
  }
}

function formatCount(value: number): string {
  return new Intl.NumberFormat(getLocale()).format(value)
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    timeZone: 'Europe/Vienna',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatHour(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    timeZone: 'Europe/Vienna',
    hour: 'numeric',
  }).format(new Date(iso))
}

function formatDay(isoDay: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium' }).format(
    new Date(`${isoDay}T12:00:00Z`),
  )
}

function busyHours(byHour: EventSummaryResponse['liveRoom']['byHour']) {
  const counted = byHour.filter((hour) => hour.checkIns > 0)
  const first = counted[0]
  const last = counted[counted.length - 1]
  if (!first || !last) return []
  const counts = new Map(byHour.map((hour) => [new Date(hour.hour).getTime(), hour.checkIns]))
  const hours: { hour: string; checkIns: number }[] = []
  const from = new Date(first.hour).getTime() - HOUR_MS
  const to = new Date(last.hour).getTime() + HOUR_MS
  for (let at = from; at <= to; at += HOUR_MS) {
    hours.push({ hour: new Date(at).toISOString(), checkIns: counts.get(at) ?? 0 })
  }
  return hours
}

function hasStarted(event: Event): boolean {
  return new Date(event.startTime).getTime() <= Date.now()
}

export function EventNumbersCard({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: event } = useEvent(businessId, eventId)
  const { data, isLoading, isError } = useEventSummary(businessId, eventId, !!event)
  const sectionRef = useRef<HTMLElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)
  const headingId = useId()
  const ended = data?.ended ?? false

  useEffect(() => {
    if (data && window.location.hash === `#${ANCHOR}`) {
      detailsRef.current?.querySelector('details')?.setAttribute('open', '')
      sectionRef.current?.scrollIntoView({ block: 'start' })
    }
  }, [data])

  if (!event) return <Skeleton className="h-40 w-full rounded-xl" />

  return (
    <section
      ref={sectionRef}
      id={ended ? ANCHOR : undefined}
      aria-labelledby={headingId}
      className="scroll-mt-6"
    >
      <Card className="space-y-4 p-4 sm:p-6">
        <h2 id={headingId} className="flex items-center gap-2 font-semibold font-serif text-lg">
          {ended ? <History className="size-5" /> : <BarChart3 className="size-5" />}
          {ended ? m.summaryTitle() : m.numbersTitle()}
        </h2>
        {isLoading && <StripSkeleton />}
        {isError && <p className="text-muted-foreground text-sm">{m.summaryLoadFailed()}</p>}
        {data && (
          <>
            <NumberStrip summary={data} event={event} />
            <div ref={detailsRef} className="border-t pt-2">
              <Disclosure title={m.numbersAll()} contentClassName="space-y-6 pb-2">
                <EventAnalyticsChart eventId={eventId} />
                <AllNumbers summary={data} event={event} />
              </Disclosure>
            </div>
          </>
        )}
      </Card>
    </section>
  )
}

function StripSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {['views', 'going', 'interested', 'checkins'].map((key) => (
        <Skeleton key={key} className="h-20 rounded-lg" />
      ))}
    </div>
  )
}

function NumberStrip({ summary, event }: { summary: EventSummaryResponse; event: Event }) {
  const tiles = [
    { key: 'views', value: summary.views.total, label: m.summaryViews() },
    { key: 'going', value: summary.answers.going, label: m.summaryGoing() },
    { key: 'interested', value: summary.answers.interested, label: m.summaryInterested() },
  ]
  if (summary.liveRoom.enabled && hasStarted(event)) {
    tiles.push({ key: 'checkIns', value: summary.liveRoom.checkIns, label: m.summaryCheckIns() })
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.key} className="rounded-lg bg-muted px-4 py-3">
          <p className="font-semibold font-serif text-2xl tabular-nums">
            {formatCount(tile.value)}
          </p>
          <p className="truncate text-muted-foreground text-sm">{tile.label}</p>
        </div>
      ))}
    </div>
  )
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm">{title}</h3>
      {children}
    </div>
  )
}

function CountRows({ rows }: { rows: { label: string; value: number }[] }) {
  const shown = rows.filter((row) => row.value > 0)
  if (shown.length === 0) return null
  return (
    <ul className="divide-y divide-border text-sm">
      {shown.map((row) => (
        <li key={row.label} className="flex items-center justify-between gap-4 py-1.5">
          <span className="truncate text-muted-foreground">{row.label}</span>
          <span className="font-medium tabular-nums">{formatCount(row.value)}</span>
        </li>
      ))}
    </ul>
  )
}

function AllNumbers({ summary, event }: { summary: EventSummaryResponse; event: Event }) {
  const answers = event.rsvpCounts
  const answerRows = answers
    ? [
        { label: m.rsvpNotGoing(), value: event.rsvpEnabled ? answers.notGoing : 0 },
        { label: m.plusOnesCount(), value: answers.plusOnes },
        {
          label: m.plusOnesTotalComing(),
          value: answers.plusOnes > 0 ? answers.going + answers.plusOnes : 0,
        },
      ]
    : []
  const funnel = event.liveRoomMode === 'off' ? undefined : event.liveRoomFunnel
  const funnelRows = funnel
    ? [
        { label: m.liveRoomFunnelScans(), value: funnel.scans },
        { label: m.liveRoomFunnelGuests(), value: funnel.guestJoins },
        { label: m.liveRoomFunnelHis(), value: funnel.his },
        { label: m.liveRoomFunnelMutuals(), value: funnel.mutuals },
        { label: m.liveRoomFunnelAccounts(), value: funnel.accounts },
      ]
    : []
  const shareCredits = summary.shareLinks.creditsConfirmed + summary.shareLinks.creditsPending
  const moreRows = [
    { label: m.summaryMoments(), value: summary.moments },
    { label: m.summaryShareVisits(), value: summary.shareLinks.visits },
    { label: m.summaryShareCredits(), value: shareCredits },
  ]

  return (
    <>
      {summary.views.bySource.length > 0 && (
        <Block title={m.summaryViewSourcesTitle()}>
          <ViewSources views={summary.views} />
        </Block>
      )}
      {answerRows.some((row) => row.value > 0) && (
        <Block title={m.eventAnswersTitle()}>
          <CountRows rows={answerRows} />
          <p className="text-muted-foreground text-xs">{m.eventAnswersAnonymous()}</p>
        </Block>
      )}
      {summary.liveRoom.checkIns > 0 && (
        <Block title={m.summaryLiveRoomTitle()}>
          <LiveRoomNumbers liveRoom={summary.liveRoom} />
        </Block>
      )}
      {funnelRows.some((row) => row.value > 0) && (
        <Block title={m.liveRoomFunnelTitle()}>
          <CountRows rows={funnelRows} />
        </Block>
      )}
      {moreRows.some((row) => row.value > 0) && (
        <Block title={m.numbersMoreTitle()}>
          <CountRows rows={moreRows} />
        </Block>
      )}
    </>
  )
}

function ViewSources({ views }: { views: EventSummaryResponse['views'] }) {
  const { bySource, total, webCountedSince } = views
  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {bySource.map((row) => (
          <li key={row.source} className="space-y-1">
            <div className="flex items-center justify-between gap-4 text-sm">
              <span className="truncate">{sourceLabel(row.source)}</span>
              <span className="text-muted-foreground tabular-nums">{formatCount(row.views)}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${total > 0 ? Math.max(2, (row.views / total) * 100) : 0}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
      {webCountedSince && (
        <p className="text-muted-foreground text-xs">
          {m.summaryWebCountedSince({ date: formatDay(webCountedSince) })}
        </p>
      )}
    </div>
  )
}

function LiveRoomNumbers({ liveRoom }: { liveRoom: EventSummaryResponse['liveRoom'] }) {
  const hours = busyHours(liveRoom.byHour)
  const highest = Math.max(1, ...hours.map((hour) => hour.checkIns))
  return (
    <div className="space-y-4">
      <CountRows rows={[{ label: m.summaryReturningTile(), value: liveRoom.returningGuests }]} />
      {liveRoom.peakAt && liveRoom.peakInside > 0 && (
        <p className="text-sm">
          {m.summaryPeakLine({
            time: formatTime(liveRoom.peakAt),
            count: formatCount(liveRoom.peakInside),
          })}
        </p>
      )}
      {hours.length > 0 && (
        <figure className="space-y-2">
          <figcaption className="text-muted-foreground text-xs">
            {m.summaryCheckInsByHour()}
          </figcaption>
          <div className="flex h-28 items-end gap-1.5">
            {hours.map((hour) => (
              <div key={hour.hour} className="flex h-full min-w-0 max-w-12 flex-1 flex-col">
                <span className="text-center text-muted-foreground text-xs tabular-nums">
                  {hour.checkIns > 0 ? formatCount(hour.checkIns) : ''}
                </span>
                <div className="flex flex-1 items-end">
                  <div
                    className="w-full rounded-t-md bg-primary"
                    style={{
                      height:
                        hour.checkIns > 0 ? `${Math.max(4, (hour.checkIns / highest) * 100)}%` : 0,
                    }}
                  />
                </div>
                <span className="truncate pt-1 text-center text-muted-foreground text-xs">
                  {formatHour(hour.hour)}
                </span>
              </div>
            ))}
          </div>
        </figure>
      )}
      {liveRoom.returningGuests > 0 && (
        <p className="text-muted-foreground text-xs">{m.summaryReturningHint()}</p>
      )}
    </div>
  )
}
